// src/components/ContactUs.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { submitChatLead } from '../services/chatLeadService';
import SEOHelmet from './SEOHelmet';
import PublicHeader from './PublicHeader';
import './ContactUs.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

const ContactUs = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    serviceReq: 'buying'
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setSuccess(true);
      setFormData({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying' });
      setTimeout(() => setSuccess(false), 5000);
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        postalCode: formData.postalCode,
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        serviceReq: formData.serviceReq,
        email: formData.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(params, { rememberContact: true });

      if (response.ok) {
        setSuccess(true);
        setFormData({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        alert(es ? 'No se pudo enviar. Inténtelo de nuevo.' : 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(es ? 'No se pudo enviar. Inténtelo de nuevo.' : 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-us-page">
      <SEOHelmet title={`Contact Giovanni & Monica Triana | ${SITE_CONFIG.AGENT.BROKERAGE}`} description="Contact Giovanni or Monica Triana about buying or selling real estate across metro Atlanta." path="/contact-us" />
      <PublicHeader />
      <button className="back-button" onClick={() => navigate('/')}>
        ← {es ? 'Volver al inicio' : 'Back to Home'}
      </button>

      <div className="contact-us-container">
        <div className="contact-us-header">
          <h1>{es ? 'Contacto' : 'Contact Us'}</h1>
          <p>{es ? '¿Listo para comprar o vender? Comuníquese hoy con Giovanni o Monica Triana.' : 'Ready to find your next home or sell your property? Get in touch with Giovanni or Monica Triana today!'}</p>
        </div>

        <div className="contact-us-content">
          <div className="contact-info-section">
            <h2>{es ? 'Comuníquese' : 'Get In Touch'}</h2>
            <div className="contact-detail">
              <span className="contact-icon">📧</span>
              <div>
                <strong>{es ? 'Correo' : 'Email'}</strong>
                <p>{SITE_CONFIG.AGENT.EMAIL}</p>
              </div>
            </div>
            <div className="contact-detail">
              <span className="contact-icon">📞</span>
              <div>
                <strong>Giovanni Triana</strong>
                <p><a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>{SITE_CONFIG.AGENT.PHONE}</a></p>
              </div>
            </div>
            <div className="contact-detail">
              <span className="contact-icon">📞</span>
              <div>
                <strong>Monica Triana</strong>
                <p><a href={`tel:${SITE_CONFIG.TEAM.MONICA.PHONE}`}>{SITE_CONFIG.TEAM.MONICA.PHONE}</a></p>
              </div>
            </div>
            <div className="contact-detail">
              <span className="contact-icon">🏠</span>
              <div>
                <strong>{es ? 'Área de servicio' : 'Service Area'}</strong>
                <p>{es ? 'Área metropolitana de Atlanta, Georgia' : 'Metro Atlanta, Georgia'}</p>
              </div>
            </div>
          </div>

          <div className="contact-form-section">
            <h2>{es ? 'Envíe un mensaje' : 'Send Us a Message'}</h2>
            {success && (
              <div className="success-message">
                ✓ {es ? 'Gracias. Nos comunicaremos pronto.' : "Thank you! We'll be in touch soon."}
              </div>
            )}
            <form onSubmit={handleSubmit}>
          <HoneypotField />
              <input
                type="text"
                placeholder={es ? 'Nombre completo *' : 'Full Name *'}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder={es ? 'Número de teléfono *' : 'Phone Number *'}
                value={formData.phoneNumber}
                onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder={es ? 'Código postal *' : 'ZIP Code *'}
                value={formData.postalCode}
                onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                required
              />
              <select
                value={formData.serviceReq}
                onChange={(e) => setFormData({...formData, serviceReq: e.target.value})}
                required
              >
                <option value="buying">{es ? 'Comprar' : 'Buying'}</option>
                <option value="selling">{es ? 'Vender' : 'Selling'}</option>
                <option value="both">{es ? 'Ambos' : 'Both'}</option>
              </select>
              <button type="submit" disabled={submitting}>
                {submitting ? (es ? 'Enviando…' : 'Submitting...') : (es ? 'Enviar' : 'Submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
